import classNames from 'classnames';
import { useCallback } from 'react';

import { Link } from '@farmersdog/corgi-legacy';
import type { TextVariant, ColorName } from '@farmersdog/corgi-x';
import { useViewport, Text } from '@farmersdog/corgi-x';
import { useCoupon, DiscountType } from '@farmersdog/coupons';

import config from '../../../config';
import { useAltGetStartedPageExperiment } from '../../../hooks/experiments';
import { AcAltGetStartedPageTreatments } from '../../../utils';
import { getSignupRoute } from '../../../utils/getSignupRoute';
import { Picture } from '../../shared';

import { mixedBowl } from './assets/mixedBowl/mixedBowl';
import { Benefits } from './Benefits';
import { Footer } from './Footer';
import styles from './GetStarted.module.css';
import { getGetStartedCopy } from './utils/copy';

import type { TOSAComponentInput, TOSALeafNode } from '../../../types';

const getInfoTextProps = (
  isMobile: boolean,
  isAltGetStartedPageExperimentOn: boolean,
  treatment: keyof typeof AcAltGetStartedPageTreatments
): { infoVariant: TextVariant; infoColor: ColorName } => {
  let variant = 'article-16';
  let color = isMobile ? 'charcoal-2' : 'charcoal-3';

  if (isMobile && !isAltGetStartedPageExperimentOn) {
    variant = 'article-12';
  }

  if (treatment === AcAltGetStartedPageTreatments.personalized_nutrition) {
    color = 'kale-2';
  }

  return {
    infoVariant: variant as TextVariant,
    infoColor: color as ColorName,
  };
};

export function GetStarted(props: TOSAComponentInput<TOSALeafNode>) {
  const { registry, formNavigation, formMethods, formSteps, useFeature } =
    props;
  const { setNext, findNext } = formNavigation;
  const { getValues } = formMethods;
  const { url, referrerRoute } = config.get('tosa.app.coreApi');
  const { discountAmount, couponCode } = useCoupon({
    apiUrl: `${url}${referrerRoute}`,
    defaultDiscount: {
      discountType: DiscountType.percentage,
      discountAmount: config.get('features.defaultTrialDiscount'),
    },
  });

  const {
    treatment: acAltGetStartedPageTreatment,
    isAltGetStartedPageExperimentOn,
  } = useAltGetStartedPageExperiment({ useFeature, couponCode });

  const variantCopy = getGetStartedCopy({
    treatment: acAltGetStartedPageTreatment,
    discountAmount,
  });

  const handleNavigateNext = useCallback(() => {
    setNext({ formSteps });
  }, [formSteps, setNext]);

  const nextSignupRoute = getSignupRoute({
    registry,
    current: findNext({ formSteps }),
    getValues,
  });

  const { md, xs } = useViewport();
  const isMobile = xs && !md;

  const { infoColor, infoVariant } = getInfoTextProps(
    isMobile,
    isAltGetStartedPageExperimentOn,
    acAltGetStartedPageTreatment
  );

  const subtitleClassnames = classNames(styles.subtitle, {
    [styles.subtitleMobile]: isMobile,
  });

  return (
    <>
      {!isMobile && !isAltGetStartedPageExperimentOn && (
        <Picture
          sources={mixedBowl}
          alt="Bowl of The Farmer’s Dog Dog Food"
          className={styles.bowl}
        />
      )}
      <div
        className={classNames(styles.textContainer, {
          [styles.experimentWrapper]: isAltGetStartedPageExperimentOn,
        })}
      >
        <Text
          as="h1"
          variant={
            isMobile && isAltGetStartedPageExperimentOn
              ? 'heading-28'
              : 'heading-40'
          }
          color="kale-3"
          bold
          className={classNames(styles.title, {
            [styles.withMaxWidth]: !isAltGetStartedPageExperimentOn,
          })}
          bottomSpacing={isMobile ? 'md' : 'none'}
          topSpacing={{
            xs: isAltGetStartedPageExperimentOn ? '4xl' : 'sm',
            md: isAltGetStartedPageExperimentOn ? 'lg' : 'xxl',
          }}
        >
          {variantCopy.title}
        </Text>
        <Text
          as="h2"
          variant={isMobile ? 'article-16' : 'article-20'}
          color="charcoal-3"
          className={subtitleClassnames}
          topSpacing={{
            xs: isMobile ? 'none' : 'sm',
            md: isAltGetStartedPageExperimentOn ? 'md' : 'sm',
          }}
          bottomSpacing={{
            xs: isAltGetStartedPageExperimentOn ? 'xl' : 'sm',
            md: isAltGetStartedPageExperimentOn ? 'xxl' : 'lg',
          }}
        >
          {variantCopy.subtitle}
        </Text>
        {!(isAltGetStartedPageExperimentOn && isMobile) && (
          <Text
            variant={infoVariant}
            fontStyle="italic"
            color={infoColor}
            as="p"
            topSpacing="none"
            bottomSpacing={{
              xs: isAltGetStartedPageExperimentOn ? 'md' : 'lg',
              md: isAltGetStartedPageExperimentOn ? 'none' : '3xl',
            }}
          >
            {variantCopy.info}
          </Text>
        )}
      </div>
      {/*
        NOTE: normally we place the submit/continue button on the form component, but because of the unique
        placement on this card and the fact that no data is submitted, we have it here instead.
      */}

      <div className={styles.buttonContainer}>
        <div className={styles.buttonBackground}>
          {isAltGetStartedPageExperimentOn && isMobile && (
            <Text
              variant={infoVariant}
              fontStyle="italic"
              color={infoColor}
              as="p"
              topSpacing="none"
              bottomSpacing={{
                xs: isAltGetStartedPageExperimentOn ? 'md' : 'lg',
                md: isAltGetStartedPageExperimentOn ? 'none' : '3xl',
              }}
            >
              {variantCopy.info}
            </Text>
          )}
          <Link
            asButton
            asPill
            to={nextSignupRoute}
            onClick={handleNavigateNext}
            aria-label="Continue to signup"
            className={classNames({
              [styles.cta]: isAltGetStartedPageExperimentOn,
            })}
          >
            {variantCopy.cta}
          </Link>
        </div>
      </div>
      {!isAltGetStartedPageExperimentOn && (
        <Benefits isMobileGetStartedCopyOn={isMobile} />
      )}
      <Footer />
    </>
  );
}
